import { QueryMethod } from "@/hooks/usePaginateQuery";
import { isDemoEnvironment } from "@/libs/store";

/**
 * Mock config: key = API method name (query or mutation), value = array of
 * param/variable matchers. Empty array = always use mock for that method when
 * the caller is inside a <MockScope>. Used by useQuery and useMutation.
 */
const config: {
  [key: string]: Array<Record<string, unknown>>;
} = {
  listApplicationsUsingGet: [{ type: "AI_AGENT" }, { type: "MCP_SERVER" }],
  getApplicationConfigurationAllFeatureUsingGet: [
    { id: "8a48827e9b9deea0019b9fcffbee2ed4" },
  ],
  listAgentAttributesUsingGet: [
    { applicationId: "8a48827e9b9deea0019b9fcffbee2ed4" },
  ],
  getApplicationUsingGet: [{ id: "8a48827e9b9deea0019b9fcffbee2ed4" }],
  listPurposesUsingGet: [],
  getMcpUserUsingGet: [],
  getSemanticModelsUsingGet: [],
  getSemanticPolicyUsingGet: [],
  getSemanticColumnsByObjectsUsingGet: [],
  listPolicyResourcesUsingGet: [
    { applicationId: "ff8080819cbbd623019cbbd6e2720002" },
    { applicationId: "mock-application-id" },
  ],
  // Mutation methods: add method name with [] to always mock, or param matchers. Add matching entry to mockRegistry for data.
  createApplicationUsingPost: [],
  // Temporary until backend adds controller-specific operationIds.
  // TODO: Remove this mock integration once enterprise policies feature is completed.
  createUsingPost: [], // EnterprisePolicyControllerService only
  listUsingGet: [], // EnterprisePolicyControllerService only
  getMcpServerNamesUsingGet: [],
  listAttributesUsingGet: [],
  "/api/metadata/8a488489887a8d360188bdb6ed9c2a84/mcpServerName": [],
  updateApplicationUsingPut: [],
  deleteApplicationUsingDelete: [],
  batchCreateResourcePoliciesUsingPost: [],
  listResourceAccessPoliciesUsingGet: [],

  // New agent experience (isNewAgentOnboarding). Its routes mount <MockScope>
  // only in demo mode (isAgentsMock), so these are mocked for demo tenants and
  // never for anyone else. All-`[]`: a param matcher would only create a silent
  // "Mock data not found" path. Feature by feature, which of these has a real
  // backend yet is tracked in `@/configs/agentFeatures`.
  // Agent registry (TRUST-13366) - live outside demo mode.
  listAgentRegistriesUsingGet: [],
  validateUsingPost: [],
  previewProjectsUsingPost: [],
  importRegistryUsingPost: [],
  syncAgentsUsingPost: [],
  deleteRegistryUsingDelete: [],
  syncAttributesUsingPost: [],
  // Real AgentControllerService endpoints (TRUST-13501).
  listEstateAgentsUsingGet: [],
  listAgentEnvironmentsUsingGet: [],
  setAgentAccessRevocationUsingPost: [],
  // Temp-service methods - no real endpoint yet.
  // TODO: Remove each once the agent estate backend ships it.
  listAgentPlatformCatalogUsingGet: [],
  testAgentEnvironmentConnectionUsingPost: [],
  testAgentIdentityConnectionUsingPost: [],
  startAgentOnboardingUsingPost: [],
  disconnectAgentEnvironmentUsingDelete: [],
  listEstateAgentAttributesUsingGet: [],
  updateEstateAgentAttributesUsingPatch: [],
  getAgentOnboardingProgressUsingGet: [],
  getAgentEstateSummaryUsingGet: [],
  getEstateAgentUsingGet: [],
  resolveAgentPolicyRecommendationsUsingPost: [],
  resolveAgentPolicyCoverageUsingPost: [],
  resolvePolicyCatalogUsingPost: [],
  getPolicyReviewUsingPost: [],
  generateDraftPolicyUsingPost: [],
  previewAgentPoliciesUsingPost: [],
  applyPoliciesUsingPost: [],
  updateAgentMissionUsingPost: [],
  getEffectiveEnforcementUsingGet: [],
  getEnforcementEnvironmentUsingGet: [],
  startAgentDiscoveryUsingPost: [],
  getAgentDiscoveryUsingGet: [],
  listDiscoveredProjectAgentsUsingGet: [],
  listOAuthClientProfilesUsingGet: [],
  getOAuthClientProfileUsingGet: [],
  getOAuthClientProfileFormOptionsUsingGet: [],
  listOAuthAuthorizationProvidersUsingGet: [],
  getOAuthAuthorizationProviderUsingGet: [],
  createOAuthClientProfileUsingPost: [],
  updateOAuthClientProfileUsingPatch: [],
  validateOAuthClientProfileUsingPost: [],
  getOAuthClientProfileValidationUsingGet: [],
  cancelOAuthClientProfileValidationUsingPost: [],
  getOAuthClientProfileUsageUsingGet: [],
  previewOAuthClientProfileImpactUsingPost: [],
  archiveOAuthClientProfileUsingPost: [],
  restoreOAuthClientProfileUsingPost: [],
  listOAuthClientProfileOptionsUsingPost: [],
  listPolicyImplementationsUsingGet: [],
  updateNativePolicyUsingPut: [],
  reapplyPolicyDefinitionUsingPost: [],
  retryPolicyDeploymentUsingPost: [],
  listAgentPoliciesUsingGet: [],
  createAgentPolicyUsingPost: [],
  updateAgentPolicyUsingPut: [],
  deleteAgentPolicyUsingDelete: [],
  // Agent Access & Protection (AGENT_FEATURES.accessProtection).
  getAgentProtectionUsingGet: [],
  listProtectionCandidatesUsingGet: [],
  getDataSourceCatalogUsingGet: [],
  associateAgentResourcesUsingPost: [],
  updateAgentResourceAssociationUsingPut: [],
  removeAgentResourceAssociationUsingDelete: [],
  saveNativePolicyDraftUsingPost: [],
  verifyNativeRoleMappingUsingPost: [],
  deployProtectionChangesUsingPost: [],
  retryFailedProtectionOperationsUsingPost: [],
  reassessAgentProtectionUsingPost: [],
  simulateGatewayCheckUsingPost: [],
  setProtectionScenarioUsingPut: [],
  resetAgentProtectionDemoUsingPost: [],

  // TrustAI Executive Decision Board: TrustAiBoardControllerService, plus the
  // mock-only methods; served from the mock store in demo mode.
  getEnterprisePostureUsingGet: [],
  listPriorityControlsUsingGet: [],
  getEstateReadinessUsingGet: [],
  startOnboardingRunUsingPost: [],
  startEvaluationUsingPost: [],
  getControlDecisionUsingGet: [],
  listAgentRegistryUsingGet: [],
  getVerifiedOutcomesUsingGet: [],
  listAuditEvidenceUsingGet: [],
  enforcePolicyUsingPost: [],
  listRelevantIntelligenceUsingGet: [],
  listGuardianRecommendationsUsingGet: [],
  listGuardianPromptsUsingGet: [],
  askGuardianUsingPost: [],
  getGuardianContextUsingGet: [],
  getEstateInventoryUsingGet: [],
  getRelationshipGraphUsingGet: [],
  getWhatsChangedUsingGet: [],
  getDataGovernanceOverviewUsingGet: [],
  listDataPlatformGovernanceUsingGet: [],
  getDataPlatformGovernanceUsingGet: [],
  getAgentFlowSummaryUsingGet: [],
  getDataGovernanceSummaryUsingGet: [],
  getAgentFleetProtectionUsingGet: [],
  getAgentActivitySummaryUsingGet: [],
  listAgentAccessFlowsUsingGet: [],
  getAgentAccessFlowUsingGet: [],
};

/**
 * Demo-only methods — these are only mocked when isDemoEnvironment() is true.
 * Checked at call time so the flag can be set dynamically without reload.
 */
const demoOnlyConfig: {
  [key: string]: Array<Record<string, unknown>>;
} = {
  getUnifiedLakehouseUserAttributesUsingGet: [],
  mapLakehouseRoleUsingPost: [],
  deployLakehousePolicyUsingPost: [],
};

/**
 * Resolve the mock decision for a method call.
 *
 * @param method - generated API client method
 * @param params - call params / mutation variables
 * @param isInMockScope - true when the caller is rendered inside a <MockScope>
 * @returns `{ isMock: true }` when the call should be served from the mock
 *   registry, otherwise `{}` to fall through to the real backend.
 */
function getMock<Args, Response>(
  method: QueryMethod<Args, Response>,
  params: Args,
  isInMockScope: boolean,
): { isMock: boolean } | object {
  if (!isInMockScope) {
    return {};
  }

  // Check demo-only methods first (evaluated at call time, not module-load time)
  if (demoOnlyConfig[method.name] && !isDemoEnvironment()) {
    return {};
  }

  const configEntries = config[method.name] ?? demoOnlyConfig[method.name];
  if (!configEntries) return {};

  const matchesParams = (entry: Record<string, unknown>): boolean => {
    const keys = Object.keys(entry) as (keyof typeof entry)[];
    // it only need to match the key value that are in the params
    return keys.every(key => {
      const value = (params as Record<string, unknown>)[key as string];
      return value !== undefined && value !== null && entry[key] === value;
    });
  };
  const matched =
    configEntries.length > 0 ? configEntries.some(matchesParams) : true;
  return matched ? { isMock: true } : {};
}

export default getMock;
